import React, { useState, useEffect, useRef } from "react";
import "./PackageSection.css";
import { FaCheck, FaTimes } from "react-icons/fa";
import api, { API_ENDPOINTS } from "../config";

const PackageSection = ({ packages }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    customerName: "",
    phoneNumber: "",
    email: "",
    paymentMethod: "",
    dateOfBirth: "",
  });
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const popoverRef = useRef(null);

  // Helper to show notification
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: "", message: "" }), 5000);
  };

  // const popoverRef = useRef(null);

  // Dynamically calculate total price
  useEffect(() => {
    if (!selectedPackage) return;

    const newTotal = Object.entries(selectedPackage.features)
      .filter(([featureName, featureData]) => selectedFeatures[featureName] && featureData.enabled)
      .reduce((sum, [, featureData]) => sum + (featureData.price || 0), 0);

    setTotalAmount(newTotal);
  }, [selectedFeatures, selectedPackage]);

  const handleOrderClick = (pkg) => {
    const initialFeatures = {};
    Object.keys(pkg.features).forEach((feature) => {
      if (!pkg.allowCustom) {
        initialFeatures[feature] = pkg.features[feature].enabled;
      } else {
        initialFeatures[feature] = pkg.features[feature].enabled;
      }
    });
    setSelectedFeatures(initialFeatures);
    setSelectedPackage(pkg);
  };

  const closePopover = () => setSelectedPackage(null);

  const handleOutsideClick = (e) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target)) {
      closePopover();
    }
  };

  useEffect(() => {
    if (selectedPackage) document.addEventListener("mousedown", handleOutsideClick);
    else document.removeEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [selectedPackage]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFeatureToggle = (featureName) => {
    setSelectedFeatures((prev) => ({
      ...prev,
      [featureName]: !prev[featureName],
    }));
  };

  const validateForm = (formData) => {
    const errors = {};

    // Date of Birth: required and must be 12+
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = "Date of birth is required.";
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 12) {
        errors.dateOfBirth = "You must be at least 12 years old to place an order.";
      }
    }

    // Name
    if (!formData.customerName.trim()) {
      errors.customerName = "Name is required.";
    } else if (!/^[A-Za-z\s]+$/.test(formData.customerName)) {
      errors.customerName = "Name should contain only letters.";
    }

    // Phone
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required.";
    } else if (!/^(?:\+251)?\d{9,15}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = "Enter a valid phone number (can start with +251).";
    }
    

    // Email
    if (
      formData.email &&
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      errors.email = "Invalid email format.";
    }

    // Payment method
    if (!formData.paymentMethod) {
      errors.paymentMethod = "Please select a payment method.";
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (!selectedPackage) return;

    if (!formData.customerName || !formData.phoneNumber || !formData.paymentMethod) {
      showNotification("error", "Please fill all required fields!");
      return;
    }

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      showNotification("error", firstError + "!");
      return;
    }

    // If custom not allowed, all enabled features are already selected
    const atLeastOneFeatureSelected = Object.entries(selectedFeatures).some(
      ([featureName, isSelected]) =>
        isSelected && selectedPackage.features[featureName]?.enabled
    );

    if (!atLeastOneFeatureSelected) {
      showNotification("error", "Please select at least one feature to place the order!");
      return;
    }

    try {
      const featuresObj = {};
      Object.keys(selectedPackage.features).forEach((featureName) => {
        const isEnabled = !!selectedFeatures[featureName];
        const price = isEnabled ? Number(selectedPackage.features[featureName].price || 0) : 0;
        featuresObj[featureName] = { enabled: isEnabled, price };
      });

      const payload = {
        customerName: formData.customerName,
        phoneNumber: formData.phoneNumber,
        email: formData.email || "",
        paymentMethod: formData.paymentMethod,
        date_of_birth: formData.dateOfBirth,
        // service: selectedPackage.pagename,
        service: selectedPackage.service?.name || selectedPackage.pagename?.servicename || "",
        packageName: selectedPackage.name,
        packageDetails: { features: featuresObj },
        totalAmount: Object.values(featuresObj).reduce(
          (sum, f) => sum + (f.enabled ? f.price : 0),
          0
        ),
      };

      console.log("Payload being sent:", payload);

      await api.post(`${API_ENDPOINTS.CREATE_ORDERS}`, payload);

      showNotification("success", "Order placed successfully ✅");

      setFormData({
        customerName: "",
        phoneNumber: "",
        email: "",
        paymentMethod: "",
        dateOfBirth: "",
      });
      setSelectedFeatures({});
      setTimeout(() => closePopover(), 1000);
    } catch (err) {
      console.error("Order failed:", err.response?.data || err);
      showNotification("error", "Order failed. Please try again. ❌");
    }
  };

  // **Group packages by service/subservice**
  const groupedPackages = packages.reduce((acc, pkg) => {
    const serviceKey = pkg.subServiceName || pkg.serviceName;
    if (!acc[serviceKey]) acc[serviceKey] = [];
    acc[serviceKey].push(pkg);
    return acc;
  }, {});

  return (
    <section className="package-section">
      {/* <h2 className="section-title">Pricing</h2> */}

      {notification.message && (
        <div className={`ik-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button className="ik-close-btn" onClick={() => setNotification({ type: "", message: "" })}>
            ✖
          </button>
        </div>
      )}

      {/* Render grouped packages */}
      {Object.keys(groupedPackages).map((groupName) => (
        <div key={groupName} className="subservice-section">
          {/* <h3 style={{ marginBottom: "20px" }}>{groupName}</h3> */}
          <div className="package-container">
            {groupedPackages[groupName].map((pkg) => {
              const packageTotal = Object.values(pkg.features || {}).reduce(
                (sum, featureData) => (featureData.enabled ? sum + (featureData.price || 0) : sum),
                0
              );

              return (
                <div key={pkg._id} className="package-card">
                  <div className="package-header">{pkg.name}</div>
                  <h3 className="package-price">{packageTotal} ETB</h3>
                  <ul className="package-features">
                    {Object.entries(pkg.features).map(([feature, data], i) => (
                      <li key={i} className={data.enabled ? "available" : "disabled-feature"}>
                        {data.enabled ? <FaCheck className="feature-icon" color="#ff7f00" /> : <FaTimes className="feature-icon" color="#999" />}
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="order-btn" onClick={() => handleOrderClick(pkg)}>
                    Order Now / Customize
                  </button>
                </div>
              );
            })}
            
          </div>
          <label htmlFor="">________________________________________________________________________________________________________________________________</label>
        </div>
      ))}

      {/* Popup */}
      {selectedPackage && (
        <div className="popover-overlay">
          <div className="popover-box" ref={popoverRef}>
            {notification.message && (
              <div className={`ik-notification ${notification.type}`}>
                <span>{notification.message}</span>
                <button className="ik-close-btn" onClick={() => setNotification({ type: "", message: "" })}>
                  ❌
                </button>
              </div>
            )}
            <button className="close-btn" onClick={closePopover}>
              <FaTimes />
            </button>

            <div className="popover-content scrollable">
              <div className="left-section">
                <h2>Place Your Order!</h2>

                <label>Your Name</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  placeholder="ስም"
                  required
                />

                <label>Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="ስልክ (0999999999)"
                  required
                />

                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ኢሜይል"
                />

                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                />

                <label>Payment</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Payment Method</option>
                  <option>Tele Birr / ቴሌ ብር</option>
                  <option>CBE / ንግድ ባንክ</option>
                  <option>AWASH BANK /አዋሽ ባንክ</option>
                </select>
                <button className="place-order-btn" onClick={handleSubmit}>
                  PLACE ORDER
                </button>
              </div>
              <div className="right-section">
                <h3>Your Order Summary</h3>
                <p>
                  <strong>Service:</strong> {selectedPackage.service?.name}
                </p>
                <p>
                  <strong>Package:</strong> {selectedPackage.name}
                </p>
                

                {/* ✅ Show features OR full-package message */}
                {!selectedPackage.allowCustom ? (
                  <div className="full-package-message">
                    This package is full — customization is not allowed.  
                    All available features are included automatically.
                  </div>
                ) : (
                  <div className="features-checkboxes">
                    <div className="customization-message"> <p>You can customize this package by selecting your preferred features below.</p></div>
                   
                    {Object.entries(selectedPackage.features).map(([featureName, featureData], i) => {
                      const isChecked = selectedFeatures[featureName] || false;
                      return (
                        <label
                          key={i}
                          className={`feature-checkbox 
                            ${featureData.enabled ? (isChecked ? "enabled" : "unchecked") : "disabled-option"}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleFeatureToggle(featureName)}
                            disabled={!featureData.enabled}
                          />
                          <span className="feature-label">
                            {featureName}{" "}
                            {featureData.enabled ? `- ${featureData.price} Birr` : "(Not available)"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <p>
                  <strong>Total Amount:</strong> {totalAmount} Birr
                </p>
                <p className="note">
                  📞 We will call you within 15 minutes to confirm your order. For urgent assistance, call <strong>+251902340084</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PackageSection;
